import React from 'react';
import {
  FaRegThumbsUp,
  FaRegThumbsDown,
  FaWrench,
  FaSyncAlt,
  FaCheckCircle,
  FaInfoCircle,
} from 'react-icons/fa';
import { useAnimatedMessageScroll } from '../hooks/useAnimatedMessageScroll';
import { useAuth } from '../hooks/useAuth';
import { MarkdownMessage } from './MarkdownMessage';
import { CustomMessage } from './CustomMessage';
import { MessageContent } from './MessageContent';
import { CopyMarkdownButton } from './CopyMarkdownButton';
import { PdfExportButton } from './PdfExportButton';
import { SavePromptFromResponseButton } from './SavePromptFromResponseButton';
import { TypingIndicator } from './TypingIndicator';
import { ChatMessage } from '../hooks/useChat';
import { useTranslation } from '../localization/useAppTranslation';
import {
  createMapLabelLocalizer,
  extractMapDisplayDataFromEvents,
} from '../utils/mapToolRegistry';
import { getAssistantPdfExportContent } from '../utils/assistantExportContent';
import type { SavedPromptFromChatRequest } from '../types/preferences';
import type { ConversationSuggestion } from '../types/conversationSuggestions';
import type { SupportedUiLocale } from '../localization/locales';
import { ConversationSuggestionChips } from './promptSuggestions/PromptSuggestionControl';
import { ThemedSpinner } from './ThemedSpinner';

const LazyMapDisplay = React.lazy(() =>
  import('./MapDisplay').then((mod) => ({ default: mod.MapDisplay }))
);
const BOT_AVATAR_SRC = '/avatars/bot-avatar.svg';
const DEFAULT_STREAM_EVENT_TYPE = 'event';
const USER_AVATAR_SRC = '/avatars/user-solid-blue.svg';

export interface MessageItemProps {
  message: ChatMessage;
  onFeedbackClick: (messageId: number, rating: number) => void;
  sourcePrompt?: SavedPromptFromChatRequest;
  onGenerateSavedPromptDraft?: (sourcePrompt: SavedPromptFromChatRequest) => Promise<void>;
  containerRef?: React.RefObject<HTMLDivElement> | any; // Reference to scroll container for animated scroll
  showInlineTypingIndicator?: boolean;
  responseLocale?: SupportedUiLocale;
  conversationSuggestionsLoading?: boolean;
  onSelectConversationSuggestion?: (suggestion: ConversationSuggestion) => void;
}

/**
 * Individual message item component.
 * Uses stream metadata to show event labels and follows live streaming updates.
 */
export const MessageItem = React.memo<MessageItemProps>(
  ({
    message,
    onFeedbackClick,
    sourcePrompt,
    onGenerateSavedPromptDraft,
    containerRef,
    showInlineTypingIndicator = false,
    responseLocale,
    conversationSuggestionsLoading = false,
    onSelectConversationSuggestion,
  }) => {
    const { t } = useTranslation('chat');
    const { t: tReports } = useTranslation('reports');
    const { authState, session, selectedFleetId, selectedFleetName, customerName } = useAuth();

    const streamEventType = message.metadata?.streamEventType;
    const streamPhase = message.metadata?.streamPhase;
    const streamEvents: Array<Record<string, any>> = Array.isArray(message.metadata?.streamEvents)
      ? message.metadata.streamEvents
      : [];
    const latestStreamEventType = streamEvents.length > 0
      ? (streamEvents[streamEvents.length - 1].type || DEFAULT_STREAM_EVENT_TYPE)
      : DEFAULT_STREAM_EVENT_TYPE;

    const getStreamEventLabel = (event?: Record<string, any>): string => {
      const eventType = event?.type || DEFAULT_STREAM_EVENT_TYPE;
      const toolName = typeof event?.toolName === 'string' ? event.toolName : undefined;

      if (eventType === 'tool_use') {
        if (toolName) {
          return t('streamEvents.toolUseWithName', { name: toolName });
        }
        return t('streamEvents.toolUse');
      }
      if (eventType === 'complete') {
        return t('streamEvents.complete');
      }
      if (eventType === 'update') {
        return t('streamEvents.streamingResponse');
      }
      if (eventType === 'reasoning') {
        return t('streamEvents.reasoning');
      }
      if (eventType === 'tool_result') {
        if (toolName) {
          return t('streamEvents.toolResultWithName', { name: toolName });
        }
        return t('streamEvents.toolResult');
      }
      if (eventType === 'response') {
        return t('streamEvents.response');
      }
      return event?.label || t('streamEvents.event');
    };

    const getEventIconForType = (eventType: string) => {
      if (eventType === 'tool_use') {
        return FaWrench;
      }
      if (eventType === 'complete') {
        return FaCheckCircle;
      }
      if (eventType === 'update') {
        return FaSyncAlt;
      }
      return FaInfoCircle;
    };

    const LatestEventIcon = getEventIconForType(latestStreamEventType);
    const latestStreamEvent = streamEvents[streamEvents.length - 1];
    const latestStreamEventLabel = getStreamEventLabel(latestStreamEvent);

    const isStreamingUpdate =
      message.direction === 'incoming' &&
      streamEventType === 'response' &&
      streamPhase === 'update';
    const canExportPdf =
      message.direction === 'incoming' &&
      !isStreamingUpdate &&
      Boolean(session?.sessionId) &&
      Boolean(message.content.trim()) &&
      !String(message.id).startsWith('error-');
    const canSaveAsPrompt =
      message.direction === 'incoming' &&
      streamPhase === 'complete' &&
      Boolean(sourcePrompt?.source_prompt) &&
      Boolean(onGenerateSavedPromptDraft) &&
      Boolean(message.content.trim()) &&
      !Boolean(message.metadata?.syntheticMessageKey) &&
      !String(message.id).startsWith('error-');
    const canCopyMarkdown =
      message.direction === 'incoming' &&
      !isStreamingUpdate &&
      Boolean(message.content.trim()) &&
      !String(message.id).startsWith('error-');
    const mapDisplayData = extractMapDisplayDataFromEvents(
      streamEvents,
      createMapLabelLocalizer(tReports),
    );
    const mapData = mapDisplayData
      ? {
          center: mapDisplayData.center,
          zoom: mapDisplayData.zoom,
          markers: mapDisplayData.markers,
        }
      : null;
    const conversationSuggestions = Array.isArray(message.metadata?.conversationSuggestions)
      ? message.metadata.conversationSuggestions as ConversationSuggestion[]
      : [];
    const conversationSuggestionLocale = message.metadata?.conversationSuggestionLocale;
    const shouldShowConversationSuggestions = (
      conversationSuggestions.length > 0
      && typeof conversationSuggestionLocale === 'string'
      && conversationSuggestionLocale === responseLocale
    );
    const shouldShowConversationSuggestionsLoading = (
      message.direction === 'incoming'
      && conversationSuggestionsLoading
    );

    // While animating, follow message as it grows with ResizeObserver
    // Hook must always be called (React rule), but only activates when animating
    useAnimatedMessageScroll(
      isStreamingUpdate ? message.id : null,
      containerRef,
      isStreamingUpdate
    );

    return (
      <CustomMessage
        id={message.id}
        direction={message.direction}
        sender={message.sender}
        content={message.content}
        className={mapData ? 'custom-message--with-map' : ''}
        timestamp={message.timestamp}
        avatarSrc={
          message.direction === 'incoming'
            ? BOT_AVATAR_SRC
            : USER_AVATAR_SRC
        }
        avatarAlt={
          message.direction === 'incoming'
            ? 'SpeedGauge'
            : authState.user?.username || t('messages.you')
        }
        showAvatar={true}
      >
        {message.direction === 'incoming' ? (
          <MessageContent content="">
            <div 
              className="bot-message-container" 
              data-message-id={message.messageId}
            >
              {(streamEvents.length > 0 || showInlineTypingIndicator) && (
                <details className="stream-events-panel">
                  <summary
                    className="stream-events-summary"
                    aria-label={t('streamEvents.latest', { label: latestStreamEventLabel })}
                  >
                    <div className="stream-events-summary-content">
                      {!showInlineTypingIndicator && (
                        <LatestEventIcon className="stream-event-icon" aria-hidden="true" />
                      )}
                      {showInlineTypingIndicator && (
                        <TypingIndicator className="typing-indicator--inline" content="" />
                      )}
                    </div>
                  </summary>
                  {streamEvents.length > 0 && (
                    <div className="stream-events-list">
                      {streamEvents.map((event, index) => {
                        const eventType = event.type || DEFAULT_STREAM_EVENT_TYPE;
                        const label = getStreamEventLabel(event);
                        const EventIcon = getEventIconForType(eventType);

                        return (
                          <div key={`${message.id}-event-${index}`} className="stream-event-item">
                            <EventIcon className="stream-event-icon" aria-hidden="true" />
                            <span className="stream-event-text">{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </details>
              )}

              <MarkdownMessage
                content={message.content}
                className="copilot-response"
                messageId={message.messageId}
              />

              {/* Render interactive map if a tool provided geo data */}
              {mapData && (
                <React.Suspense fallback={<div className="map-loading">{t('map.loading')}</div>}>
                  <LazyMapDisplay
                    center={mapData.center}
                    zoom={mapData.zoom}
                    markers={mapData.markers}
                  />
                </React.Suspense>
              )}

              {/* Render citations if available */}
              {message.citations && message.citations.length > 0 && (
                <details className="message-citations">
                  <summary>{t('citations.summary', { count: message.citations.length })}</summary>
                  <ul className="citations-list">
                    {message.citations.map((citation, index) => (
                      <li key={index} className="citation-item">
                        <MarkdownMessage
                          content={citation}
                          className="citation-content"
                        />
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {shouldShowConversationSuggestions && (
                <div className="message-conversation-suggestions">
                  <ConversationSuggestionChips
                    suggestions={conversationSuggestions}
                    responseLocale={conversationSuggestionLocale as SupportedUiLocale}
                    onSelect={(suggestion) => onSelectConversationSuggestion?.(suggestion)}
                  />
                </div>
              )}

              {shouldShowConversationSuggestionsLoading && (
                <div
                  className="message-conversation-suggestions message-conversation-suggestions--loading"
                  aria-busy="true"
                >
                  <ThemedSpinner size="md" ariaLabel={t('suggestions.loading')} />
                </div>
              )}

              {(canExportPdf || canCopyMarkdown || message.messageId || canSaveAsPrompt) && (
                <div className="message-footer message-actions">
                  {/* Feedback actions lead the assistant message action row. */}
                  {message.messageId && (
                    <div className="message-feedback-buttons">
                      <button
                        type="button"
                        className="message-action-button feedback-btn-inline"
                        onClick={() => onFeedbackClick(message.messageId!, 1)}
                        aria-label={t('feedback.helpfulAriaLabel')}
                        title={t('feedback.helpfulTitle')}
                      >
                        <FaRegThumbsUp aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="message-action-button feedback-btn-inline"
                        onClick={() => onFeedbackClick(message.messageId!, 0)}
                        aria-label={t('feedback.notHelpfulAriaLabel')}
                        title={t('feedback.notHelpfulTitle')}
                      >
                        <FaRegThumbsDown aria-hidden="true" />
                      </button>
                    </div>
                  )}
                  {canExportPdf && (
                    <PdfExportButton
                      showLabel={false}
                      className="message-action-button"
                      getExportData={() => ({
                        content: getAssistantPdfExportContent(message.content, message.metadata),
                        citations: message.citations,
                        messageId: message.messageId || message.id,
                        sessionId: session!.sessionId,
                        timestamp: message.timestamp,
                        userName: authState.user?.username,
                        customerId: session?.customerId || authState.user?.customerId,
                        customerName: customerName || session?.customerName,
                        vehicleGroupId: selectedFleetId,
                        vehicleGroupName: selectedFleetName,
                        mapData,
                        mapTitle: mapDisplayData?.title,
                      })}
                    />
                  )}
                  {canSaveAsPrompt && sourcePrompt && onGenerateSavedPromptDraft && (
                    <SavePromptFromResponseButton
                      sourcePrompt={sourcePrompt}
                      onGenerateDraft={onGenerateSavedPromptDraft}
                    />
                  )}
                  {canCopyMarkdown && (
                    <CopyMarkdownButton
                      content={message.content}
                      showLabel={false}
                      className="message-action-button"
                    />
                  )}
                </div>
              )}
            </div>
          </MessageContent>
        ) : (
          <MessageContent content={message.content} />
        )}
      </CustomMessage>
    );
  }
);

MessageItem.displayName = 'MessageItem';
